<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable" :columns="columnDefs"
                  :settings="{ hideTable: true, hideExport: true}"
                  endpoint="reports/examinations/number-of-examinations"
                  @onDataLoaded="onDataLoaded">
      <template #filter>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="fullName"
            :placeholder="t('selectOptometrist')"
            v-model="selectedEmployee"
            @change="onFilterChanged"
        >
          <el-option
              v-for="option in employees"
              :key="option.id"
              :label="option.fullName"
              :value="option"
          ></el-option>
        </el-select>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            :placeholder="t('selectOphthalmologist')"
            v-model="selectedOphthalmologist"
            @change="onFilterChanged"
        >
          <el-option
              v-for="option in ophthalmologists"
              :key="option"
              :label="option"
              :value="option"
          ></el-option>
        </el-select>
        <el-select
            v-if="useSettingsStore().getValue('show_insurance_companies_button_on_companies_page') == 'true'"
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="id"
            :placeholder="t('selectInsuranceCompany')"
            v-model="selectedInsurance"
            @change="onFilterChanged"
        >
          <el-option
              v-for="ins in insurances"
              :key="ins.id"
              :label="ins.name"
              :value="ins"
          ></el-option>
        </el-select>
        <el-button type="primary" @click="print">{{ t('print') }}</el-button>
      </template>
      <template #cards>
        <div id="printable" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div>
              <el-card shadow="never">
                <div class="flex flex-col items-center">
                  <div class="text-lg print:text-sm text-gray-500">{{
                      t('totalExaminations')
                    }}
                  </div>
                  <div class="text-2xl print:text-lg pt-2">{{ totalExaminations }}</div>
                </div>
              </el-card>
            </div>
            <div v-if="examinations && examinations.length"
                 class="overflow-scroll bg-background rounded-l">
              <div class="border border-gray-200">
                <div
                    class="flex bg-gray-100 font-semibold text-sm print:text-xs text-gray-700 border-b border-gray-300 p-2">
                  <div class="w-1/6">{{ t('examinationNumber') }}</div>
                  <div class="w-1/6">{{ t('customer') }}</div>
                  <div class="w-1/6">{{ t('optometrist') }}</div>
                  <div class="w-1/6">{{ t('ophthalmologist') }}</div>
                  <div v-if="useSettingsStore().getValue('show_insurance_companies_button_on_companies_page') == 'true'" class="w-1/6">{{ t('insuranceCompany') }}</div>
                  <div class="w-1/6">{{ t('date') }}</div>
                </div>
                <div
                    v-for="examination in examinations"
                    :key="examination.id"
                    class="flex border-b border-gray-200 text-sm print:text-xs p-2"
                >
                  <div class="w-1/6">{{ examination.externalID }}</div>
                  <div class="w-1/6">{{ examination.customer.name }}</div>
                  <div class="w-1/6">{{ examination.examinator?.name }}</div>
                  <div class="w-1/6">{{ examination.doctor }}</div>
                  <div v-if="useSettingsStore().getValue('show_insurance_companies_button_on_companies_page') == 'true'" class="w-1/6">{{ examination.insurance?.name }}</div>
                  <div class="w-1/6">{{
                      formatPostgresDate(String(examination.createdAt), "YYYY-MM-DD hh:mm A")
                    }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div>
            <div>
              <el-card shadow="never">
                <div class="flex flex-col items-center">
                  <div class="text-lg print:text-xs text-gray-500">{{
                      t('totalAmount')
                    }}
                  </div>
                  <div class="text-2xl print:text-lg pt-2">
                    {{ formatMoney(totalAmount, $ctx.currency) }}
                  </div>
                </div>
              </el-card>
            </div>


            <div v-if="orderItems && orderItems.length"
                 class="overflow-scroll bg-background rounded-l">
              <div class="border border-gray-200">
                <div
                    class="flex bg-gray-100 font-semibold text-sm print:text-xs text-gray-700 border-b border-gray-300 p-2">
                  <div class="w-[12%]">{{ t('orderNumber') }}</div>
                  <div class="w-[28%]">{{ t('customer') }}</div>
                  <div class="w-[20%]">{{ t('item') }}</div>
                  <div class="w-[15%]">{{ t('price') }}</div>
                  <div class="w-[25%]">{{ t('date') }}</div>
                </div>
                <div
                    v-for="oi in orderItems"
                    :key="oi.orderItem.id"
                    class="flex border-b border-gray-200 text-sm print:text-xs p-2"
                >
                  <div class="w-[12%]">{{ oi.orderNumber }}</div>
                  <div class="w-[28%]">{{ oi.customerName || t('customer') }}</div>
                  <div class="w-[20%]">{{ oi.orderItem?.item?.name }}</div>
                  <div class="w-[15%]">{{
                      formatMoney(oi.orderItem.soldAtPriceAmount * (oi.orderItem.quantity - oi.orderItem.returnedQuantity), oi.orderItem.currency).replace("IQD", "")
                    }}
                  </div>
                  <div class="w-[25%]">{{
                      formatPostgresDate(String(oi.orderItem.createdAt), "YYYY-MM-DD hh:mm A")
                    }}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ColDef} from 'ag-grid-community'
import {useHTTP} from '@/plugins/http'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import User from '@/models/user/User'
import {formatMoney} from "../../../../utils/money";
import Examination from "@/models/customer/Examination";
import Insurance from "@/models/insurance/Insurance";
import {OrderItem} from "@/models/sales/Order";
import {formatPostgresDate} from "../../../../utils/date";
import {useSettingsStore} from "@/stores/settingsStore";

// i18n
const {t} = useI18n({
  messages: {
    en: {
      optometrist: 'Optometrist',
      selectOptometrist: 'Select Optometrist',
      ophthalmologist: 'Ophthalmologist',
      selectOphthalmologist: 'Select Ophthalmologist',
      insuranceCompany: 'Insurer',
      selectInsuranceCompany: 'Select Insurer',
      totalExaminations: 'Total Examinations',
      totalAmount: "Total Amount",
      examinationNumber: 'Examination No',
      customer: 'Customer',
      orderNumber: 'Order No',
      price: 'Price',
      date: 'Date',
      item: 'Item',
      print: 'Print'
    },
    ar: {
      optometrist: 'الفاحص',
      selectOptometrist: 'اختر الفاحص',
      ophthalmologist: 'طبيب العيون',
      selectOphthalmologist: 'اختر طبيب العيون',
      insuranceCompany: 'شركة التأمين',
      selectInsuranceCompany: 'اختر شركة التأمين',
      totalExaminations: 'مجموع الفحوصات',
      totalAmount: "المبلغ الكلي",
      examinationNumber: 'رقم الفحص',
      customer: 'الزبون',
      orderNumber: 'رقم الطلب',
      price: 'السعر',
      date: 'التاريخ',
      item: 'المنتج',
      print: 'طباعة'
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])
const http = useHTTP()

const employees = ref<User[] | any>()
const selectedEmployee = ref<any>()

const ophthalmologists = ref<string[]>([])
const selectedOphthalmologist = ref<string>()

const insurances = ref<Insurance[]>([])
const selectedInsurance = ref<Insurance>()

const totalExaminations = ref(0)
const totalAmount = ref(0)

const examinations = ref<Examination[]>([])
const orderItems = ref<{
  customerName: string;
  orderNumber: string;
  orderItem: OrderItem
}[]>([])

onBeforeMount(async () => {
  columnDefs.value = []

  // Load optometrists
  try {
    const employeesRes = await http.get<User[]>(`${User.ENDPOINT}/optometrists`)
    employees.value = employeesRes.map((employee) => User.from(employee))
  } catch (error) {
    employees.value = []
  }

  // Load insurance companies
  try {
    const insurancesRes = await http.get<Insurance[]>(Insurance.ENDPOINT)
    insurances.value = insurancesRes.map((ins) => Insurance.from(ins))
  } catch (error) {
    insurances.value = []
  }

  // Load ophthalmologists from settings
  const settingsStore = useSettingsStore()
  const ophthalmologistsValue = settingsStore.getValue('ophthalmologists')
  if (ophthalmologistsValue) {
    try {
      ophthalmologists.value = typeof ophthalmologistsValue === 'string'
        ? JSON.parse(ophthalmologistsValue)
        : ophthalmologistsValue
    } catch (e) {
      console.error('Error parsing ophthalmologists:', e)
      ophthalmologists.value = []
    }
  }
})

async function onFilterChanged() {
  rtable.value.payload.filter = selectedEmployee.value ? selectedEmployee.value.id : null;
  rtable.value.payload.doctor = selectedOphthalmologist.value || null;
  rtable.value.payload.insurance_id = selectedInsurance.value?.id || null;
  rtable.value.navigate(rtable.value.payload)
}

function onDataLoaded(data: any) {
  totalExaminations.value = 0
  totalExaminations.value = data.total

  totalAmount.value = 0
  totalAmount.value = data.totalAmount

  orderItems.value = data.examinationOrderItems;
  examinations.value = data.examinations;
}

function print() {
  window.print();
}
</script>
<style>
@media print {

  #printable {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  #printable > * {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  html, body {
    margin: 0;
    padding: 0;
  }

  #printable {
    width: 210mm;
    overflow: scroll;
    background-color: #fff;
  }

  #printable,
  #printable * {
    visibility: visible;
  }

  #printable {
    visibility: visible;
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
    margin: 0;
  }
}
</style>
